import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysDataExchange } from './queryKeys';

export type DataExchangeContactedPerson = {
  readonly email: string;
  readonly firstName: string;
  readonly id: UUID;
  readonly lastName: string;
  readonly phoneNumber: string;
  readonly position: string;
};

export type DataExchangeContactingLink = {
  readonly dataExchangeId: UUID;
  readonly id: UUID;
  readonly lastAccess: string; // ISO 8601 date string
  readonly link: string;
  readonly status: string;
  readonly stillValidFor: number;
  readonly validFor: number;
};

export type DataExchangeResponsiblePerson = {
  readonly id: UUID;
  readonly email: string;
  readonly firstName: string;
  readonly lastName: string;
};

export type DataExchangeContactChannel = 'email' | 'personal' | 'phone';

export type DataExchangeContactAttempt = {
  readonly channel: DataExchangeContactChannel;
  readonly createdById: UUID;
  readonly createdOn: string; // ISO 8601 date string
  readonly dataExchangeId: UUID;
  readonly id: UUID;
  readonly notes: string;
  readonly timestamp: string; // ISO 8601 date string
};

export type DataExchange = {
  readonly activeCustomerIds: UUID[];
  readonly contactAttempts: DataExchangeContactAttempt[];
  readonly contactedPerson: DataExchangeContactedPerson;
  readonly contactingLink: DataExchangeContactingLink;
  readonly exchangeInvoices: boolean;
  readonly exchangeLfs: boolean;
  readonly exchangeOrders: boolean;
  readonly furtherInformation: string;
  readonly id: UUID;
  readonly isExchangeViaPaper: boolean;
  readonly knownIssues: string;
  readonly receiverId: UUID;
  readonly responsiblePerson: DataExchangeResponsiblePerson;
  readonly senderId: UUID;
};

/**
 * Fetches the data exchange with the given data exchange ID from the API.
 * @see https://app.dev.vestigas.com/redoc#tag/Data-Exchange/operation/get_data_exchange_for_id_data_exchange_query__data_exchange_id__get
 */
export const fetchDataExchange = async (dataExchangeId: UUID) => {
  const response = await vestigasApi
    .get(ENDPOINT.DATA_EXCHANGE.GET(dataExchangeId))
    .json<DataExchange>();

  return response;
};

/**
 * React Query based custom hook for getting the data for a data exchange with the given data exchange ID.
 */
export const useQueryDataExchange = (
  dataExchangeId: UUID,
  options?: Omit<UseQueryOptions<DataExchange>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery({
    enabled: Boolean(dataExchangeId),
    queryFn: async () => fetchDataExchange(dataExchangeId),
    queryKey: queryKeysDataExchange.get(dataExchangeId),
    staleTime: 0, // always refetch
    ...options,
  });
};
